// src/utils/apiInference.js

/**
 * Recursively process a parsed JSON value:
 * - strings      → "{{ key }}"
 * - numbers/bool → {{ key }}
 * - objects      → process each property recursively
 * - arrays       → process the first element (if object) and wrap in []
 */
const replaceValuesWithPlaceholders = (key, value) => {
  if (typeof value === "string") {
    return `{{ ${key} }}`;
  }
  if (typeof value === "number" || typeof value === "boolean" || value === null) {
    return `{{ ${key} }}`;
  }
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
    // assume homogeneous array – process the first element
    const processedItem = replaceValuesWithPlaceholders(key, value[0]);
    return [processedItem];
  }
  if (typeof value === "object" && value !== null) {
    const result = {};
    Object.entries(value).forEach(([childKey, childVal]) => {
      result[childKey] = replaceValuesWithPlaceholders(childKey, childVal);
    });
    return result;
  }
  // fallback (shouldn't happen)
  return `{{ ${key} }}`;
};

export const inferConfigFromSample = (sample) => {
  const {
    sampleUrl = "",
    sampleMethod = "POST",
    sampleHeaders = {},
    sampleQueryParams = {},
    samplePayload = "{}",
  } = sample;

  // ---------- 1. URL ----------
  //
  // The Sample Data tab owns the URL. Whatever the user typed — or
  // whatever the cURL importer resolved a path variable to — is exactly
  // what should appear here. We deliberately do NOT re-detect numeric
  // or UUID-shaped segments and wrap them in `{{ un_named_N }}`
  // placeholders.
  //
  // Rationale: the URL field on the Sample tab is the single source of
  // truth for the endpoint. A user who wants a variable in the path
  // writes `{{ booking_id }}` there themselves, or picks "give it a
  // name" in the cURL importer. A user who wants the literal (e.g.
  // `/properties/2`) leaves the literal there, or picks "Not a
  // variable (keep original value)" in the cURL importer. Either way,
  // re-deriving placeholders here would silently override that choice
  // — which is the exact regression this comment documents.
  const inferredUrl = sampleUrl;

  // ---------- 2. Method ----------
  const method = sampleMethod.toUpperCase();

  // ---------- 3. Headers ----------
  const inferredHeaders = {};
  Object.entries(sampleHeaders).forEach(([key, value]) => {
    if (key.toLowerCase() === "authorization" && !value.includes("{{")) {
      const parts = value.split(/\s+/);
      if (parts.length > 1) {
        const scheme = parts.slice(0, -1).join(" ");
        inferredHeaders[key] = `${scheme} {{ token }}`;
      } else {
        inferredHeaders[key] = "{{ token }}";
      }
    } else {
      inferredHeaders[key] = value;
    }
  });

  // ---------- 4. Query Parameters ----------
  const inferredQuery = {};
  Object.entries(sampleQueryParams).forEach(([key, value]) => {
    if (!value.includes("{{")) {
      inferredQuery[key] = `{{ ${key} }}`;
    } else {
      inferredQuery[key] = value;
    }
  });

  // ---------- 5. Payload ----------
  let inferredPayload = samplePayload;
  try {
    const parsed = JSON.parse(samplePayload);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const processed = {};
      Object.entries(parsed).forEach(([key, value]) => {
        processed[key] = replaceValuesWithPlaceholders(key, value);
      });
      inferredPayload = JSON.stringify(processed);
    }
  } catch (e) {
    // keep as is
  }

  return {
    apiUrl: inferredUrl,
    method,
    headers: inferredHeaders,
    queryParams: inferredQuery,
    payloadTemplate: inferredPayload,
  };
};